import { useSelector } from 'react-redux';

import { CountryConfigNames } from '../../../../../contexts/country-config-context/country-config-enums';
import { useEnableInsurancePromoExtended } from '../../../../../feature-flags/hooks/use-enable-insurance-promo-extended';
import { useShipFroms } from '../../../../../hooks/ship-froms/use-ship-froms';
import { useCountryConfig } from '../../../../../hooks/use-country-config';
import { useHasDemoOrdersFeature } from '../../../../../hooks/use-has-demo-orders-feature';
import Global from '../../../../../store/global-selectors';
import { useWallet } from '../../../../../wallet/hooks/use-wallet';
import { todoCardIds } from '../todo-card-ids';

/**
 * Simple visibility rules for todo cards.
 * Each rule returns true if the card should be SHOWN, false if it should be HIDDEN.
 * @type {{[key: number]: (options: object) => boolean}}
 */
const VISIBILITY_RULES = {
  [todoCardIds.FREEMIUM_CONNECT_STORE]: (options) => !options.hasStores,
  [todoCardIds.FREEMIUM_ADD_SHIP_FROM_ADDRESS]: (options) => !options.hasShipFroms,
  [todoCardIds.FREEMIUM_ADD_PAYMENT_METHOD]: (options) => !options.isWalletRegistered,
  [todoCardIds.DEMO_ORDER]: (options) => options.enableDemoOrdersFlag,
  [todoCardIds.SETUP_INSURANCE]: (options) => options.enableInsurancePromo,
  [todoCardIds.IMPORT_PRODUCTS]: () => true, // Always show for now - can add product import checking logic later
  [todoCardIds.SETUP_RETURNS]: () => true, // Always show for now - can add returns setup checking logic later
  [todoCardIds.PHONE_NUMBER_CAPTURE]: (options) => !options.hasPhoneNumber,
};

/**
 * Determines if a todo card should be shown based on various filtering criteria
 * @param {object} todoItem - The todo card item to check
 * @param {object} [filterOptions] - Filtering criteria
 * @param {string} [filterOptions.pageWhereRendered] - Filter by page where rendered
 * @param {boolean} [filterOptions.advancedOptionsTodo] - Filter by advanced options flag
 * @param {string[]} [filterOptions.todoIdsInLocalState] - Filter by todo IDs in local state
 * @param {boolean} [filterOptions.hasStores] - Whether user has active non-manual stores (matches useWizardData logic)
 * @param {boolean} [filterOptions.hasShipFroms] - Whether user has ship from addresses (matches useWizardData logic)
 * @param {boolean} [filterOptions.isWalletRegistered] - Whether wallet is registered for visibility checks
 * @param {boolean} [filterOptions.enableDemoOrdersFlag] - Feature flag for demo orders
 * @param {number[]} [filterOptions.hiddenTodoCardIds] - Array of todo card IDs that should be hidden based on country config
 * @returns {boolean} - Whether the todo card should be shown
 */
export const shouldShowTodoCard = (todoItem, filterOptions = {}) => {
  const { pageWhereRendered, advancedOptionsTodo, todoIdsInLocalState, hiddenTodoCardIds } = filterOptions;

  const shouldCardBeRenderedOnThisPage = !pageWhereRendered || todoItem.pageWhereRendered === pageWhereRendered;
  if (!shouldCardBeRenderedOnThisPage) {
    return false;
  }

  if (advancedOptionsTodo !== undefined && todoItem.advancedOptionsTodo !== advancedOptionsTodo) {
    return false;
  }

  if (todoIdsInLocalState && !todoIdsInLocalState.includes(todoItem.id)) {
    return false;
  }

  if (hiddenTodoCardIds && hiddenTodoCardIds.includes(todoItem.id)) {
    return false;
  }

  const visibilityRule = VISIBILITY_RULES[todoItem.id];
  if (visibilityRule && !visibilityRule(filterOptions)) {
    return false;
  }

  return true;
};

/**
 * Hook that returns filtered todo cards based on various criteria.
 * Automatically fetches the seller's home country from the Redux store and evaluates visibility checks.
 * @param {Array} todoCards - Array of todo card items to filter
 * @param {object} [filterOptions] - Additional filtering criteria
 * @param {string} [filterOptions.pageWhereRendered] - Filter by page where rendered
 * @param {boolean} [filterOptions.advancedOptionsTodo] - Filter by advanced options flag
 * @param {string[]} [filterOptions.todoIdsInLocalState] - Filter by todo IDs in local state
 * @returns {Array} - Filtered array of todo cards that should be shown
 */
export const useFilteredTodoCards = (todoCards, filterOptions = {}) => {
  const enableDemoOrdersFlag = useHasDemoOrdersFeature();
  const enableInsurancePromo = useEnableInsurancePromoExtended();
  const { isWalletRegistered } = useWallet();
  const shipFroms = useShipFroms();

  const [, hiddenTodoCardsData] = useCountryConfig(CountryConfigNames.FTUWizardHiddenTodoCards);
  const hiddenTodoCardIds = hiddenTodoCardsData?.todoCardIds ?? [];

  // Get data using the same logic as useWizardData
  const stores = useSelector(Global.selectSellerStores);
  const hasStores = stores?.filter?.((s) => s?.isActive && !s?.isManualStore)?.length > 0 || false;
  const hasShipFroms = shipFroms?.length > 0 || false;
  const sellerPhoneNumber = useSelector(Global.selectSellerPhoneNumber);
  const hasPhoneNumber = Boolean(sellerPhoneNumber);

  return todoCards.filter((item) =>
    shouldShowTodoCard(item, {
      ...filterOptions,
      hasStores,
      hasShipFroms,
      isWalletRegistered,
      enableDemoOrdersFlag,
      enableInsurancePromo,
      hiddenTodoCardIds,
      hasPhoneNumber,
    }),
  );
};
